import { Button } from "@/components/ui/baseUi/button"
import { DropdownMenuItem } from "@/components/ui/baseUi/dropdown-menu"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import type { BoxSessionFormikEntity } from "@/modules/boxSlotSession/entities/boxSessionFormikEntity"
import { cn } from "@/modules/lib/utils"
import { ScoreType } from "@/modules/scores/scoreTypes"
import { SessionBlockExerciseEntity } from "@/modules/sessions/entities/sessionBlockExerciseEntity"
import type {
  ProgramInfiniteSessionFormikType,
  ProgramOnOffSessionFormikType,
  ProgramSessionInputKey,
} from "@/modules/sessions/programSessionFormikType"
import { getDisplayExerciseCategory } from "@/modules/sessions/sessionDetails/blocks/exerciseBlock/exerciseUtils"
import { RpeTag } from "@/modules/sessions/sessionDetails/components/rpeTag"
import {
  convertChronoFormikToChronoDraft,
  type ExerciseFormikType,
} from "@/modules/sessions/sessionForm/blocks/exerciseBlock/exerciseFormikTypes"
import { useBlockDuration } from "@/modules/sessions/sessionForm/hooks/useBlockDuration"
import { useSessionSidebar } from "@/modules/sessions/sessionForm/hooks/useSessionSidebar"
import { type SessionBlockExerciseFormikType } from "@/modules/sessions/sessionForm/sessionBlockFormikTypes"
import { useSessionExerciseCopyActions } from "@/modules/sessions/stores/useSessionExerciseCopyStore"
import { useSidebar } from "@/modules/sidebar/hooks/useSidebar"
import { useCreateExerciseTemplate } from "@/modules/template/hooks/useTemplate"
import type { SimpleFormik } from "@/typings/formik"
import { ArrowIcon } from "@images/svgIcons/arrowIcon"
import { ChronoIcon } from "@images/svgIcons/chronoIcon"
import { CopyIcon } from "@images/svgIcons/copyIcon"
import { EditIcon } from "@images/svgIcons/editIcon"
import { TrashIcon } from "@images/svgIcons/trashIcon"
import { convertChronoDraftIntoChrono, displayTotalTime, getTrainingChronoTitle } from "@modules/chrono/chronoUtils"
import { word } from "@modules/core/utils/i18n"
import { getScoreResultTypeCaption, getScoreTypeLabel } from "@modules/scores/scoreUtils"
import { EllipsisMenu } from "@modules/ui/components/ellipsisMenu"
import { HURichTextView } from "@modules/ui/components/richText/huRichTextView"
import { formatTextToRichText } from "@modules/ui/components/richText/richTextUtils"
import { ClipboardCheckIcon, Video } from "lucide-react"
import { useMemo } from "react"

type exerciseExistingBlocProps = {
  formik:
    | SimpleFormik<BoxSessionFormikEntity, ProgramSessionInputKey>
    | SimpleFormik<ProgramInfiniteSessionFormikType, ProgramSessionInputKey>
    | SimpleFormik<ProgramOnOffSessionFormikType, ProgramSessionInputKey>
  block: SessionBlockExerciseFormikType
  blockIndex: number
  moveBlock: (blockIndex: number, direction: "UP" | "DOWN") => Promise<void>
  deleteBlock: (blockIndex: number) => Promise<void>
  showTemplateActions?: boolean
  className?: string
} & React.ComponentProps<"div">

export function ExerciseExistingBloc({
  formik,
  block,
  moveBlock,
  deleteBlock,
  blockIndex,
  showTemplateActions = true,
  className,
  ...props
}: exerciseExistingBlocProps) {
  const { getBlockDurationInterval } = useBlockDuration(formik.values.blocks)
  const { navigateToExerciseForm } = useSessionSidebar()
  const { setExerciseToCopy } = useSessionExerciseCopyActions()
  const { hideSidebarStackOnTop } = useSidebar()
  const { mutateAsync: createExerciseTemplate } = useCreateExerciseTemplate()

  const copyBlock = (blockIndex: number) => {
    try {
      setExerciseToCopy(formik.values.blocks[blockIndex]?.content as ExerciseFormikType)

      toast.success(word("program.exercise.copy.success"), {
        classNames: successToastClassName(),
      })
    } catch {
      toast.error(word("program.exercise.copy.error"), {
        classNames: errorToastClassName(),
      })
    }
  }

  const handleSaveAsTemplate = () => {
    void createExerciseTemplate({
      name: block.content.category,
      description: null,
      templateCategoryId: null,
      exerciseId: block.content.id,
      exerciseContent: SessionBlockExerciseEntity.fromFormikToInputDto(block.content, 0),
    })
      .then(() => {
        toast.success(word("template.toast.created.success"), {
          classNames: successToastClassName(),
        })
      })
      .catch(() => {
        toast.error(word("template.toast.created.error"), {
          classNames: errorToastClassName(),
        })
      })
  }

  const displayChronoTime = useMemo(() => {
    const chrono = convertChronoDraftIntoChrono(block.content) || block.content.chrono

    if (block.content.chronoType !== "NONE" && chrono) {
      const chronoDraft = convertChronoFormikToChronoDraft(block.content)
      return (
        getTrainingChronoTitle(block.content.chronoType) +
        " - " +
        (chronoDraft ? displayTotalTime(chronoDraft) : word("chrono.title.no_chrono"))
      )
    }
    return word("chrono.title.no_chrono")
  }, [block.content])

  const displayScoreType = useMemo(() => {
    if (block.content.scoreSetConfigurations.length === 0) return null
    if (block.content.scoreSetConfigurations.length === 1 && !!block.content.scoreSetConfigurations[0])
      return getScoreTypeLabel(block.content.scoreSetConfigurations[0].scoreType)
    if (
      block.content.scoreSetConfigurations.length > 1 &&
      block.content.scoringOption?.startsWith("SET_N_FOR_SCOREBOARD_")
    ) {
      const splitValue = block.content.scoringOption.split("_")
      const indexString = splitValue[splitValue.length - 1]
      if (indexString === undefined) {
        throw new Error("Invalid scoring option value")
      }
      const index = parseInt(indexString)
      if (!block.content.scoreSetConfigurations[index]) throw new Error("Invalid scoring option value")

      return getScoreTypeLabel(block.content.scoreSetConfigurations[index].scoreType)
    }
    if (
      block.content.scoreSetConfigurations.length > 1 &&
      !!block.content.scoreSetConfigurations[0] &&
      !!block.content.scoringOption &&
      block.content.scoreSetConfigurations.every(
        (scoreSet) => scoreSet.scoreType === block.content.scoreSetConfigurations[0]?.scoreType,
      )
    ) {
      return getScoreTypeLabel(block.content.scoreSetConfigurations[0].scoreType)
    }
    return ""
  }, [block.content])

  const displayScoreTypeCaption = useMemo(() => {
    if (
      block.content.scoreSetConfigurations.length > 1 &&
      !!block.content.scoringOption &&
      block.content.scoreSetConfigurations.every(
        (scoreSet) => scoreSet.scoreType === block.content.scoreSetConfigurations[0]?.scoreType,
      )
    ) {
      return getScoreResultTypeCaption(block.content.scoringOption, true)
    }
    return ""
  }, [block.content])

  return (
    <div className={cn(className)} {...props}>
      {/* Block duration interval */}
      {!!block.content.durationInMin && (
        <Text fontStyle="BS" className="mb-1 flex flex-row items-center gap-2">
          <div className="bg-neutral-700" style={{ width: 10, height: 10, borderRadius: 3 }} />
          {getBlockDurationInterval(blockIndex)}
        </Text>
      )}

      <div className={"flex flex-col gap-3 rounded-2xl bg-surface-foreground px-5 py-6"}>
        {/* Exercise card header */}
        <div className="flex flex-col">
          <Text fontStyle="TS" className="text-textDefault-body">
            {getDisplayExerciseCategory(block.content.category, block.content.otherCategory)}
          </Text>
        </div>

        {/* Exercise card content */}
        <div className="flex flex-col">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center">
              <ChronoIcon color="currentColor" className="text-textDefault-body" />
              <Text className="ml-2" fontStyle="LM">
                {displayChronoTime}
              </Text>
            </div>

            {block.content.rpe !== null && <RpeTag exercise={block.content} />}
          </div>

          <HURichTextView value={formatTextToRichText(block.content.description)} paragrapheFontStyle="BM" />

          {block.content.scoreSetConfigurations.length > 0 &&
            !!block.content.scoreSetConfigurations[0] &&
            ![ScoreType.TEXT_FROM_ATHLETE, ScoreType.NO_SCORE].includes(
              block.content.scoreSetConfigurations[0].scoreType,
            ) && (
              <div className="mt-4 mb-2 flex w-full items-center gap-2">
                <div className="flex flex-col">
                  <Text fontStyle="LM">{word("program.session.score")}</Text>
                  {displayScoreTypeCaption && (
                    <Text fontStyle="LXS" className="mr-2">
                      {displayScoreTypeCaption}
                    </Text>
                  )}
                </div>

                <div className="mb-1 flex-1 border-b border-neutral-100" />

                <Text fontStyle="LL" className="mr-2 font-bold">
                  {displayScoreType}
                </Text>
              </div>
            )}

          {block.content.requiredMedia && (
            <div className="mt-4 mb-2 flex flex-row items-center justify-start gap-2">
              <Video className="text-textError" color="currentColor" />
              <Text fontStyle="BS">{word("program.planning.create.exercise.media_requirement.required")}</Text>
            </div>
          )}
        </div>

        {/* Exercise card footer*/}
        <div className="flex flex-row items-center justify-end gap-1">
          <EllipsisMenu propsContent={{ side: "bottom" }}>
            {blockIndex !== 0 && formik.values.blocks.length >= 1 && (
              <DropdownMenuItem key="moveUp" onClick={() => void moveBlock(blockIndex, "UP")}>
                <ArrowIcon arrowDirection="top" color="currentColor" width={16} height={16} />
                {word("sessionForm.blocks.move.up")}
              </DropdownMenuItem>
            )}
            {blockIndex < formik.values.blocks.length - 1 && formik.values.blocks.length >= 1 && (
              <DropdownMenuItem key="moveDown" onClick={() => void moveBlock(blockIndex, "DOWN")}>
                <ArrowIcon arrowDirection="bottom" color="currentColor" width={16} height={16} />
                {word("sessionForm.blocks.move.down")}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              key="copyBlock"
              onClick={() => {
                copyBlock(blockIndex)
              }}
            >
              <CopyIcon color="currentColor" width={20} height={20} />
              {word("program.exercise.copy.label")}
            </DropdownMenuItem>
            {showTemplateActions && (
              <DropdownMenuItem
                key="saveAsTemplate"
                onClick={() => {
                  handleSaveAsTemplate()
                }}
              >
                <ClipboardCheckIcon color="currentColor" width={20} height={20} />
                {word("template.exercise.text.saveAs")}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              key="deleteBlock"
              onClick={() => {
                void deleteBlock(blockIndex)
              }}
            >
              <TrashIcon color="currentColor" width={20} height={20} />
              {word("sessionForm.exercise.remove_exo.button")}
            </DropdownMenuItem>
          </EllipsisMenu>

          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  size="iconMd"
                  variant="outline"
                  onClick={() => {
                    navigateToExerciseForm(block.content, blockIndex, async (exercise: ExerciseFormikType) => {
                      await formik.setFieldValue(
                        "blocks",
                        formik.values.blocks.map((block, index) =>
                          index === blockIndex ? { ...block, content: exercise } : block,
                        ),
                      )
                      hideSidebarStackOnTop()
                    })
                  }}
                >
                  <EditIcon color="currentColor" width={16} height={16} className="text-textDefault-heading" />
                </Button>
              }
            />
            <TooltipContent side="bottom">
              <Text fontStyle="LM" className="text-textDefault-body">
                {word("global.edit")}
              </Text>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}
